import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled, { keyframes } from 'styled-components';

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Page = styled.div`
  min-height: 100vh;
  background: radial-gradient(ellipse 120% 80% at 50% -20%, rgba(56, 189, 248, 0.12), transparent 50%),
    linear-gradient(180deg, #070b12 0%, #0a101c 40%, #06090f 100%);
  color: #e2e8f0;
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
  background: rgba(7, 11, 18, 0.75);
  backdrop-filter: blur(12px);
`;

const Logo = styled.span`
  font-weight: 700;
  font-size: 1.125rem;
  letter-spacing: -0.02em;
  color: #f8fafc;
`;

const Nav = styled.nav`
  display: none;
  gap: 2rem;
  @media (min-width: 768px) {
    display: flex;
    align-items: center;
  }
`;

const NavLink = styled.a`
  font-size: 0.875rem;
  color: #94a3b8;
  transition: color 0.2s;

  &:hover {
    color: #38bdf8;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const GhostButton = styled(Link)`
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #cbd5e1;
  border-radius: 9999px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  transition: border-color 0.2s, color 0.2s, background 0.2s;

  &:hover {
    color: #f8fafc;
    border-color: rgba(56, 189, 248, 0.5);
    background: rgba(56, 189, 248, 0.08);
  }
`;

const PrimaryButton = styled(Link)`
  padding: 0.5rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #0a101c;
  background: linear-gradient(135deg, #38bdf8 0%, #0ea5e9 100%);
  border-radius: 9999px;
  box-shadow: 0 4px 20px rgba(14, 165, 233, 0.35);
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 28px rgba(14, 165, 233, 0.45);
  }
`;

const Hero = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 1.5rem 5rem;
  text-align: center;

  @media (min-width: 768px) {
    padding: 5.5rem 1.5rem 6rem;
  }
`;

const HeroBadge = styled.p`
  display: inline-block;
  margin-bottom: 1.25rem;
  padding: 0.35rem 0.85rem;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #7dd3fc;
  border: 1px solid rgba(56, 189, 248, 0.35);
  border-radius: 9999px;
  background: rgba(56, 189, 248, 0.08);
  animation: ${fadeUp} 0.6s ease both;
`;

const HeroTitle = styled.h1`
  max-width: 900px;
  margin: 0 auto 1.25rem;
  font-size: clamp(2rem, 5vw, 3.25rem);
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.03em;
  color: #f8fafc;
  animation: ${fadeUp} 0.65s ease 0.05s both;
`;

const HeroAccent = styled.span`
  background: linear-gradient(135deg, #38bdf8, #a78bfa);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const HeroLead = styled.p`
  max-width: 640px;
  margin: 0 auto 2rem;
  font-size: 1.0625rem;
  line-height: 1.65;
  color: #94a3b8;
  animation: ${fadeUp} 0.7s ease 0.1s both;
`;

const HeroCtas = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
  animation: ${fadeUp} 0.75s ease 0.15s both;
`;

const HeroPrimary = styled(Link)`
  padding: 0.875rem 1.75rem;
  font-size: 1rem;
  font-weight: 600;
  color: #0a101c;
  background: linear-gradient(135deg, #38bdf8 0%, #22d3ee 100%);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(34, 211, 238, 0.3);
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(34, 211, 238, 0.4);
  }
`;

const HeroSecondary = styled(Link)`
  padding: 0.875rem 1.75rem;
  font-size: 1rem;
  font-weight: 600;
  color: #e2e8f0;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(15, 23, 42, 0.5);
  transition: border-color 0.2s, background 0.2s;

  &:hover {
    border-color: rgba(56, 189, 248, 0.45);
    background: rgba(56, 189, 248, 0.06);
  }
`;

const Stats = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem 4rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  align-items: stretch;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StatCard = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  padding: 1.4rem;
  text-align: center;
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.12);
  background: rgba(15, 23, 42, 0.45);
`;

const StatValue = styled.div`
  flex-shrink: 0;
  font-size: clamp(2rem, 4vw, 2.75rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #38bdf8;
`;

const StatLabel = styled.div`
  flex: 1;
  margin-top: 0.5rem;
  font-size: 0.9375rem;
  color: #94a3b8;
  line-height: 1.5;
  text-align: center;
  min-width: 0;

  strong {
    font-weight: 600;
    color: #e2e8f0;
  }
`;

const Section = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 3.5rem 1.5rem;
`;

const SectionTitle = styled.h2`
  margin-bottom: 0.75rem;
  font-size: clamp(1.5rem, 3vw, 2rem);
  font-weight: 700;
  text-align: center;
  color: #f8fafc;
`;

const SectionLead = styled.p`
  max-width: 560px;
  margin: 0 auto 2.5rem;
  text-align: center;
  font-size: 1rem;
  color: #94a3b8;
  line-height: 1.6;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const FeatureCard = styled.div`
  padding: 1.5rem;
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.12);
  background: linear-gradient(165deg, rgba(30, 41, 59, 0.5) 0%, rgba(15, 23, 42, 0.65) 100%);
  transition: border-color 0.2s, transform 0.2s;

  &:hover {
    border-color: rgba(56, 189, 248, 0.25);
    transform: translateY(-2px);
  }
`;

const FeatureIcon = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  border-radius: 10px;
  background: rgba(56, 189, 248, 0.15);
  color: #7dd3fc;
`;

const FeatureTitle = styled.h3`
  margin-bottom: 0.5rem;
  font-size: 1.0625rem;
  font-weight: 600;
  color: #f1f5f9;
`;

const FeatureText = styled.p`
  font-size: 0.9375rem;
  line-height: 1.55;
  color: #94a3b8;
`;

const Steps = styled.ol`
  list-style: none;
  max-width: 720px;
  margin: 0 auto;
  padding: 0;
  counter-reset: step;
`;

const Step = styled.li`
  position: relative;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 1.25rem;
  padding: 1.25rem 0;
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);

  &:last-child {
    border-bottom: none;
  }

  &::before {
    counter-increment: step;
    content: counter(step);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    font-weight: 700;
    font-size: 0.9375rem;
    color: #0a101c;
    background: linear-gradient(135deg, #38bdf8, #22d3ee);
    border-radius: 50%;
    flex-shrink: 0;
  }
`;

const StepBody = styled.div``;

const StepTitle = styled.h3`
  margin-bottom: 0.35rem;
  font-size: 1.0625rem;
  font-weight: 600;
  color: #f1f5f9;
`;

const StepText = styled.p`
  font-size: 0.9375rem;
  line-height: 1.55;
  color: #94a3b8;
`;

const CtaBand = styled.section`
  max-width: 1200px;
  margin: 0 auto 4rem;
  padding: 0 1.5rem;
`;

const CtaInner = styled.div`
  padding: 2.5rem 1.5rem;
  text-align: center;
  border-radius: 20px;
  border: 1px solid rgba(56, 189, 248, 0.25);
  background: linear-gradient(135deg, rgba(14, 165, 233, 0.12) 0%, rgba(99, 102, 241, 0.1) 100%);
`;

const CtaTitle = styled.h2`
  margin-bottom: 0.75rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: #f8fafc;
`;

const CtaText = styled.p`
  margin-bottom: 1.5rem;
  font-size: 1rem;
  color: #cbd5e1;
  max-width: 480px;
  margin-left: auto;
  margin-right: auto;
`;

const Footer = styled.footer`
  padding: 2rem 1.5rem;
  text-align: center;
  font-size: 0.8125rem;
  color: #64748b;
  border-top: 1px solid rgba(148, 163, 184, 0.1);
`;

function LandingPage() {
  const { isAuthenticated } = useSelector((state) => state.auth);

  return (
    <Page>
      <Header>
        <Logo>Trade Bot</Logo>
        <Nav>
          <NavLink href="#product">Product</NavLink>
          <NavLink href="#advantages">Advantages</NavLink>
          <NavLink href="#start">How to start</NavLink>
        </Nav>
        <HeaderActions>
          {isAuthenticated ? (
            <PrimaryButton to="/dashboard">Dashboard</PrimaryButton>
          ) : (
            <>
              <GhostButton to="/login">Sign in</GhostButton>
              <PrimaryButton to="/login">Get started</PrimaryButton>
            </>
          )}
        </HeaderActions>
      </Header>

      <Hero>
        <HeroBadge>Stablecoin pairs · Risk-free bot</HeroBadge>
        <HeroTitle>
          Trade <HeroAccent>USDT / USDC</HeroAccent> and other stablecoin markets - no wild altcoin swings
        </HeroTitle>
        <HeroLead>
          This bot only works stablecoin-to-stablecoin pairs (for example USDT/USDC). Both legs stay pegged to the
          dollar, so you are not taking directional crypto risk like on BTC or ETH - it is built as a risk-free bot
          from a price-volatility standpoint. We currently work only with <strong>MEXC</strong> and{' '}
          <strong>Binance</strong>: on MEXC the bot targets <strong>10-15% APY</strong>, on Binance{' '}
          <strong>5-7% APY</strong> (venue-specific spreads and fees). Connect with API keys, keep funds on the
          exchange, and trade with withdrawal disabled on the key.
        </HeroLead>
        <HeroCtas>
          {isAuthenticated ? (
            <HeroPrimary to="/dashboard">Dashboard</HeroPrimary>
          ) : (
            <>
              <HeroPrimary to="/login">Open dashboard</HeroPrimary>
              <HeroSecondary to="/login">Sign in</HeroSecondary>
            </>
          )}
        </HeroCtas>
      </Hero>

      <Stats>
        <StatsGrid>
          <StatCard>
            <StatValue>USDT↔USDC</StatValue>
            <StatLabel>
              Core focus on dollar-pegged pairs and similar stablecoin crosses - liquidity and tight spreads instead of
              token narratives.
            </StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>Risk-free*</StatValue>
            <StatLabel>
              No long/short exposure to volatile coins: the idea is to earn from stable-to-stable dynamics, not from
              guessing the next pump.
            </StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>MEXC</StatValue>
            <StatLabel>
              Supported today - stable APY band we target on MEXC stablecoin pairs is <strong>10-15%</strong> annualized.
            </StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>Binance</StatValue>
            <StatLabel>
              Supported today - on Binance we currently target <strong>5-7% APY</strong> on the same style of stable
              markets.
            </StatLabel>
          </StatCard>
        </StatsGrid>
      </Stats>

      <Section id="product">
        <SectionTitle>Stablecoin-only bot</SectionTitle>
        <SectionLead>
          The product is scoped to stablecoin markets so you are not running a directional crypto strategy. That is
          what we mean by a risk-free bot in practice: both sides of the pair are designed to hold a stable dollar
          value. We only integrate <strong>MEXC</strong> and <strong>Binance</strong> right now - target APY is about{' '}
          <strong>10-15%</strong> on MEXC and <strong>5-7%</strong> on Binance for capital you allocate to these stable
          pairs.
        </SectionLead>
        <FeatureGrid>
          <FeatureCard>
            <FeatureIcon>◈</FeatureIcon>
            <FeatureTitle>USDT, USDC, and peers</FeatureTitle>
            <FeatureText>
              The bot trades stablecoin pairs such as USDT/USDC where MEXC or Binance list them. You are not opening
              exposure to floating altcoins or layer-1 price risk.
            </FeatureText>
          </FeatureCard>
          <FeatureCard>
            <FeatureIcon>◇</FeatureIcon>
            <FeatureTitle>No transfer of funds</FeatureTitle>
            <FeatureText>
              You fund your exchange account only. The bot connects via API and never asks you to send assets to a
              third party.
            </FeatureText>
          </FeatureCard>
          <FeatureCard>
            <FeatureIcon>◎</FeatureIcon>
            <FeatureTitle>Systematic, peg-aware logic</FeatureTitle>
            <FeatureText>
              Rules-based execution around tight stable-to-stable ranges - built for repeat small edges rather than
              betting on macro crypto moves.
            </FeatureText>
          </FeatureCard>
          <FeatureCard>
            <FeatureIcon>⚡</FeatureIcon>
            <FeatureTitle>Light footprint</FeatureTitle>
            <FeatureText>
              Because both legs are stables, the strategy does not rely on large leveraged bets on volatile
              underlyings; sizing stays understandable.
            </FeatureText>
          </FeatureCard>
          <FeatureCard>
            <FeatureIcon>◐</FeatureIcon>
            <FeatureTitle>P&L in stables</FeatureTitle>
            <FeatureText>
              Performance is measured in stable terms - aligned with how you already think about cash on the exchange.
            </FeatureText>
          </FeatureCard>
          <FeatureCard>
            <FeatureIcon>✓</FeatureIcon>
            <FeatureTitle>MEXC and Binance only</FeatureTitle>
            <FeatureText>
              We currently work only with these two exchanges. Use trade-only API keys (withdrawals off) - funds never
              leave your exchange wallet. Target APY: about 10-15% on MEXC, 5-7% on Binance, depending on how you size
              stable pairs.
            </FeatureText>
          </FeatureCard>
        </FeatureGrid>
      </Section>

      <Section id="advantages">
        <SectionTitle>Why stablecoin bots feel risk-free</SectionTitle>
        <SectionLead>
          When both assets in the pair aim for $1, you are not trying to time Bitcoin or memecoins. The bot automates
          that narrow universe so you get discipline without headline volatility.
        </SectionLead>
        <FeatureGrid>
          <FeatureCard>
            <FeatureTitle>No coin “to the moon” stress</FeatureTitle>
            <FeatureText>
              You are not holding a long into a crash or a short into a squeeze on a volatile token - just stables vs
              stables on the rails your exchange provides.
            </FeatureText>
          </FeatureCard>
          <FeatureCard>
            <FeatureTitle>Emotion-free execution</FeatureTitle>
            <FeatureText>
              The program follows its rules on USDT/USDC-style markets around the clock - no panic clicks when a chart
              gaps 20% overnight on an alt.
            </FeatureText>
          </FeatureCard>
          <FeatureCard>
            <FeatureTitle>Many small, bounded actions</FeatureTitle>
            <FeatureText>
              Automation can refresh quotes and manage inventory in stable pairs far more often than a human would,
              still within the same low-volatility premise.
            </FeatureText>
          </FeatureCard>
        </FeatureGrid>
      </Section>

      <Section id="start">
        <SectionTitle>Start in minutes</SectionTitle>
        <SectionLead>
          A short onboarding path: open a <strong>MEXC</strong> or <strong>Binance</strong> account with stable
          balances, create API keys, sign in here - then enable stablecoin pairs like USDT/USDC and start the bot.
        </SectionLead>
        <Steps>
          <Step>
            <StepBody>
              <StepTitle>Create or open your MEXC or Binance account</StepTitle>
              <StepText>
                We only support these two venues today. Complete KYC where required and fund the account you control
                with stables.
              </StepText>
            </StepBody>
          </Step>
          <Step>
            <StepBody>
              <StepTitle>Generate API keys</StepTitle>
              <StepText>
                Enable trading permissions and disable withdrawals. Store secrets securely - your dashboard will use
                them to connect.
              </StepText>
            </StepBody>
          </Step>
          <Step>
            <StepBody>
              <StepTitle>Sign in here</StepTitle>
              <StepText>Use your Trade Bot account to reach the dashboard and link your integration.</StepText>
            </StepBody>
          </Step>
          <Step>
            <StepBody>
              <StepTitle>Configure stable pairs and launch</StepTitle>
              <StepText>
                Choose USDT/USDC (or other stable crosses listed on your MEXC or Binance account), confirm sizing, then
                start the risk-free stablecoin bot when you are ready.
              </StepText>
            </StepBody>
          </Step>
        </Steps>
      </Section>

      <CtaBand>
        <CtaInner>
          <CtaTitle>Ready to open the dashboard?</CtaTitle>
          <CtaText>
            Sign in to link MEXC or Binance API keys - stablecoin pairs like USDT/USDC, no volatile-token exposure by
            design. Expect roughly <strong>10-15% APY</strong> on MEXC and <strong>5-7% APY</strong> on Binance.
          </CtaText>
          <HeroPrimary to={isAuthenticated ? '/dashboard' : '/login'}>
            {isAuthenticated ? 'Dashboard' : 'Go to login'}
          </HeroPrimary>
        </CtaInner>
      </CtaBand>

      <Footer>
        © {new Date().getFullYear()} Trade Bot. *“Risk-free” refers to avoiding directional crypto price risk on
        stablecoin pairs; depeg, exchange, smart-contract, and operational risks can still apply. APY ranges differ
        by venue (MEXC vs Binance) and are illustrative targets, not guarantees. Not financial advice.
      </Footer>
    </Page>
  );
}

export default LandingPage;
